import { Fragment, JSX, ReactNode, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Breadcrumbs,
  Button,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { capitalizeFirstLetter } from "~/lib/helpers";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { K8NestedServiceAccountsSection } from "./components/K8NestedServiceAccountsSection";
import { ContentFlag } from "~/components/content-flag";
import { Markdown } from "~/components/markdown";
import {
  AccessTime as AccessTimeIcon,
  ArrowForward as ArrowForwardIcon,
  Home as HomeIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import {
  ClientIntegrationType,
  GetClientIntegrationQuery,
  GetIntegrationsSummaryDocument,
  IntegrationMessageStatus,
  ListClientIntegrationsDocument,
  useDeleteClientIntegrationMutation,
  useGetClientIntegrationDeleteCommandQuery,
  useGetClientIntegrationQuery,
} from "~/operations";
import { pluralize } from "~/lib/pluralize";
import { useSnackbar } from "notistack";
import { FormatDate } from "~/lib/date";
import { GetInfoIcon } from "~/lib/info-icons";
import { getColor } from "~/lib/colors";
import { CanUpdate } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Update";
import { setDocumentTitle } from "~/lib/commonUtils";
import { ConfirmationDialog } from "~/components/ConfirmationDialog";
import {
  ContainersGradientIcon,
  NodesGradientIcon,
  OthersGradientIcon,
  PodsGradientIcon,
} from "~/icons";
import { integrationInventoryAssetsUrl } from "~/pages/integrations/utils/integrationInventoryAssetsUrl";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

type Integration =
  GetClientIntegrationQuery["clientIntegration"]["integration"];
type AssetCounts = NonNullable<Integration["assetCounts"]>;

export interface K8IntegrationPageProps {
  scope: SpaceScope | OrganizationScope;
}

export function K8IntegrationPage({ scope }: K8IntegrationPageProps) {
  const { integrationId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  let navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const mrn = getIntegrationMrn({ scope, integrationId });
  const { data, error, loading } = useGetClientIntegrationQuery({
    variables: { mrn },
  });

  const deleteCommandData = useGetClientIntegrationDeleteCommandQuery({
    variables: { input: { mrn: scope.mrn, type: ClientIntegrationType.K8S } },
  });

  const [deleteIntegration] = useDeleteClientIntegrationMutation({
    refetchQueries: [
      ListClientIntegrationsDocument,
      GetIntegrationsSummaryDocument,
    ],
  });

  const { integration } = data?.clientIntegration || {};

  setDocumentTitle([
    integration?.name,
    "Kubernetes",
    "Integrations",
    scope.name,
  ]);

  if (error) {
    return <LoadingFailedPage what="Integration" />;
  }

  if (loading || !integration) {
    return <LoadingPage what="Integration" />;
  }

  // integration
  let integrationStatus: Integration["status"] = integration.status;
  if (integration.status.toLowerCase() === "not_ready") {
    integrationStatus = "pending";
  }

  // need full iconset
  const getIcon = (name: string): JSX.Element => {
    switch (name.toLowerCase()) {
      case "containers":
        return <ContainersGradientIcon sx={{ fontSize: 40 }} />;
      case "nodes":
        return <NodesGradientIcon sx={{ fontSize: 40 }} />;
      case "pods":
        return <PodsGradientIcon sx={{ fontSize: 40 }} />;
      default:
        return <OthersGradientIcon sx={{ fontSize: 40 }} />;
    }
  };

  type CountsObject = { name: string; total: number; icon: JSX.Element };

  const groupsList = ["k8s.pod", "asset", "container_image"];
  const notedGroups =
    integration.assetCounts?.filter((x) => groupsList.includes(x.assetType)) ||
    [];
  const totalAssets =
    integration.assetCounts
      ?.filter((x) => x.assetType === "all")
      .map((y) => y.total)
      .pop() || 0;

  const buildAssetCounts = (group: AssetCounts): CountsObject[] => {
    const formatGroupName = (assetType: AssetCounts[0]["assetType"]) => {
      let formattedType: string;
      const regex = new RegExp("k8s.");

      switch (assetType.toLowerCase()) {
        case "asset":
          formattedType = "node";
          break;
        case "container_image":
          formattedType = "container";
          break;
        default:
          formattedType = assetType;
          break;
      }
      formattedType = pluralize(formattedType.replace(regex, ""), 2);
      return formattedType;
    };

    // Return the counts of each of our most interested groups
    // Pods, Nodes, and Containers
    const notedCounts = group
      .filter((x) => x.total > 0)
      .map((y) => {
        const name = formatGroupName(y?.assetType);
        const total = y?.total || 0;
        const icon = getIcon(name);
        return { name, total, icon };
      });

    // Calculate the value of all other groups.  We don't return all of them
    // in the API, so the formula for this is to subtract the total of our
    // Noted groups from the total of all existing groups.
    const totalNoted = notedCounts.reduce((agg, x) => agg + x.total, 0);
    const totalOther = totalAssets - totalNoted;
    const otherCounts = {
      name: "Others",
      total: totalOther,
      icon: getIcon("others"),
    };

    if (totalOther > 0) {
      return [...notedCounts, otherCounts];
    }
    return notedCounts;
  };

  const assetCounts = buildAssetCounts(notedGroups);

  // needs types -> codegen throwing error
  const buildEvaluatedConfigurations = (
    evaluatedConfigurations: Integration["evaluatedConfigurations"],
  ) => {
    if (!evaluatedConfigurations) return [];

    const transformConfigName = (name?: string | null) => {
      if (!name) return "";
      return name.split(/(?=[A-Z])/).join(" ");
    };

    const sortByName = (a: string, b: string): number => {
      if (a > b) {
        return 1;
      } else if (a < b) {
        return -1;
      } else {
        return 0;
      }
    };

    const evaled = evaluatedConfigurations.map((x) => {
      const mismatch = Boolean(x.configured !== x.applied);

      let configStatus = "Pending";
      if (x.configured === true) {
        configStatus = "Enabled";
      } else if (x.configured === false) {
        configStatus = "Disabled";
      }

      return {
        id: x.option,
        name: transformConfigName(x?.option),
        mismatch,
        configStatus,
        // Message/Status regarding possible mismatch of settings that
        // have been configured but not yet applied
        evaledStatus: x.msgStatus,
        evaledMsg: x.msg,
        // Message/Status regarding possible errors or warnings coming from
        // integration checkins
        operatorStatus: x.operatorMsgStatus || "NONE",
        operatorMsg: x.operatorMsg,
      };
    });

    evaled.sort((a, b) => sortByName(a.name, b.name));

    // Add the Operator version to the set of configurations
    // because its data is coming from a different portion of the API
    evaled.unshift({
      id: "OperatorVersion",
      name: "Operator Version",
      mismatch: false,
      configStatus: integration.lastStateInfo?.operatorVersion || "unknown",
      evaledStatus: IntegrationMessageStatus.Info,
      evaledMsg: "",
      operatorStatus: IntegrationMessageStatus.Info,
      operatorMsg: integration.lastStateInfo?.operatorVersion || "unknown",
    });

    return evaled;
  };

  const evaluatedConfigs = buildEvaluatedConfigurations(
    integration?.evaluatedConfigurations,
  );

  const nameSpaceDisplay = integration.lastStateInfo?.namespace
    ? `${integration.lastStateInfo.namespace} / `
    : "";

  // Delete Functions
  const DELETE_COPY =
    "To remove the Mondoo Kubernetes Integration, paste the following code into your CLI:\n\n```bash\n%COMMAND%\n```\n";

  const handleDelete = async () => {
    try {
      await deleteIntegration({
        variables: { input: { mrn: integration.mrn } },
      });
      enqueueSnackbar(`Successfully removed Kubernetes cluster from Mondoo`, {
        variant: "success",
      });
      navigate(`/space/integrations/kubernetes?${scope.params}`);
    } catch {
      enqueueSnackbar(`Could not remove Kubernetes cluster from Mondoo`, {
        variant: "error",
      });
    }
  };

  const deleteDetails = () => {
    const deleteCommand =
      deleteCommandData.data?.getClientIntegrationDeleteCommand.message;
    const finalCommand = deleteCommand
      ? deleteCommand
      : "Error retrieving command";
    const finalCopy = DELETE_COPY.replace(/\%COMMAND\%/g, finalCommand);
    return <Markdown source={finalCopy} />;
  };

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?${scope.params}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Link
      key="/space/integrations"
      component={RouterLink}
      to={`/space/integrations?${scope.params}`}
    >
      Integrations
    </Link>,
    <Link
      key="/space/integrations/kubernetes"
      component={RouterLink}
      to={`/space/integrations/kubernetes?${scope.params}`}
    >
      Kubernetes
    </Link>,
    // Namespace will only display if present
    // > <namespace> / <name>
    <Typography key={1} color="text.primary">
      {nameSpaceDisplay}
      {integration.name}
    </Typography>,
  ];

  const hasError =
    integration.messages?.find(
      (message) => message.status === IntegrationMessageStatus.Error,
    ) ||
    integration.evaluatedConfigurations?.find(
      (config) => config.operatorMsgStatus === IntegrationMessageStatus.Error,
    );

  // If we have an error, we need to extract it from the integration messages
  // or the evaluated configurations
  let errorMsg = null;
  if (
    hasError?.__typename === "IntegrationMessage" &&
    hasError.message.length > 0
  ) {
    errorMsg = hasError?.message;
  } else if (hasError?.__typename === "EvaluatedConfiguration") {
    errorMsg = hasError?.operatorMsg;
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 5 }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>
      {/* Header Block */}
      <Grid
        container
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "inherit", sm: "flex-start" },
          justifyContent: "space-between",
          mb: 6.5,
        }}
      >
        {errorMsg && (
          <Grid sx={{ mb: 3 }} size={12}>
            <Alert severity="error" variant="outlined">
              <AlertTitle>Error</AlertTitle>
              {capitalizeFirstLetter(errorMsg)}
            </Alert>
          </Grid>
        )}
        <Grid
          container
          size={{
            xs: "grow",
            sm: 12,
            md: 8,
          }}
        >
          <Grid
            sx={{
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            <Typography
              variant="h1"
              fontSize={32}
              fontWeight={700}
              sx={{ mr: 4, textTransform: "uppercase", lineHeight: "inherit" }}
            >
              {integration.name}
            </Typography>
          </Grid>
          <Grid sx={{ mb: 1 }} size={12}>
            <ContentFlag flag={integrationStatus} color={integration.status} />
          </Grid>

          <Grid container columnGap={2} mb={{ sm: 1, md: 0 }}>
            <Grid
              size={{
                xs: 12,
                sm: "auto",
              }}
            >
              <AccessTimeIcon
                sx={{
                  fontSize: 16,
                  height: 28,
                  color: "text.secondary",
                  verticalAlign: "middle",
                }}
              />
              <Typography
                display="inline"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 0.75 }}
              >
                {/* Last scanned: April 05, 10:10am */}
                Last scanned: {FormatDate(integration.lastCheckin)}
              </Typography>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: "auto",
              }}
            >
              {integration.createdBy?.name != "" && (
                <Fragment>
                  <PersonIcon
                    sx={{
                      fontSize: 16,
                      height: 28,
                      color: "text.secondary",
                      verticalAlign: "middle",
                    }}
                  />
                  <Typography
                    display="inline"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 0.75 }}
                  >
                    Created by: {integration.createdBy?.name}
                  </Typography>
                </Fragment>
              )}
            </Grid>
          </Grid>
        </Grid>
        {/* Don't show the delete or edit buttons if a user does not have permission */}
        <CanUpdate>
          <Grid sx={{ mt: { xs: 2, sm: 0 }, alignSelf: "flex-start" }}>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDialogOpen(true)}
            >
              Delete
            </Button>
            <Button
              component={RouterLink}
              to={`/space/integrations/kubernetes/${integrationId}/edit?${scope.params}`}
              variant="outlined"
              color="secondary"
              sx={{ ml: 2 }}
            >
              Edit Configuration
            </Button>
          </Grid>
        </CanUpdate>
      </Grid>
      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          mb: 4,
        }}
      >
        <Typography variant="h5" component="h2" fontWeight={700}>
          Integration Details
        </Typography>
        {/* If the integration status is pending, we don't want to show a link to assets */}
        {integrationStatus !== "pending" && totalAssets > 0 && (
          <Button
            color="secondary"
            component={RouterLink}
            to={integrationInventoryAssetsUrl(scope.id, integration.mrn).href}
            endIcon={<ArrowForwardIcon />}
          >
            Show Assets
          </Button>
        )}
      </Box>
      {/* Groupings */}
      <Grid container component={Paper} mb={3} p={1}>
        <Grid sx={{ p: 2 }} size={12}>
          <Typography
            variant="body2"
            fontWeight={500}
            sx={{
              textTransform: "uppercase",
              borderBottom: "1px solid",
              borderColor: (theme) => theme.palette.background.lightest,
              pb: 2,
            }}
          >
            Discovered Resources ({totalAssets})
          </Typography>
        </Grid>
        {totalAssets < 1 ? (
          <NoDiscoveredAssets />
        ) : (
          assetCounts?.map((x) => (
            <Grid
              key={x.name}
              size={{
                xs: 12,
                sm: 6,
                md: 3,
              }}
            >
              <Grid container sx={{ p: 2 }}>
                <Grid sx={{ mr: 2 }}>{x.icon}</Grid>
                <Grid size="grow">
                  <Typography
                    fontWeight={700}
                    sx={{ mb: -0.75, textTransform: "capitalize" }}
                  >
                    {x.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    textTransform="uppercase"
                  >
                    {x.total} Discovered
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ))
        )}
      </Grid>
      {/* Configuration and Overview Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid
          container
          columnSpacing={{ xs: 0, sm: 3 }}
          rowSpacing={{ xs: 3, sm: 0 }}
        >
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <KubernetesDataTable title="Operator Configuration">
              {evaluatedConfigs.map((config) => (
                <TableRow key={config.id}>
                  <TableCell
                    sx={{
                      pt: 1,
                      pl: 0,
                      pb: 0,
                    }}
                    width="50%"
                  >
                    {config.name}
                  </TableCell>
                  <TableCell
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      pt: 1,
                      pl: 0,
                      pb: 0,
                    }}
                    width="50%"
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        display: "inline-block",
                        mr: 1,
                        color: (theme) =>
                          config.mismatch
                            ? modifiedGetColor(theme, config.evaledStatus)
                            : "text.primary",
                      }}
                    >
                      {config.configStatus}
                    </Typography>
                    <Tooltip
                      title={config.evaledMsg || ""}
                      placement="top"
                      arrow
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {config.mismatch && (
                          <GetInfoIcon status={config.evaledStatus} />
                        )}
                      </Box>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </KubernetesDataTable>
          </Grid>
          {/* Labels and Tags */}
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <KubernetesDataTable title="Platform Overview Information">
              <TableRow>
                <TableCell
                  sx={{ pt: 1, pl: 0, pb: 0, color: "text.secondary" }}
                  width="50%"
                >
                  Kubernetes Version
                </TableCell>
                <TableCell sx={{ pt: 1, pl: 0, pb: 0 }} width="50%">
                  {integration.lastStateInfo?.k8sVersion}
                </TableCell>
              </TableRow>
            </KubernetesDataTable>
          </Grid>
        </Grid>
      </Paper>
      {/* Service Accounts */}
      <K8NestedServiceAccountsSection {...{ scope, mrn }} />
      <ConfirmationDialog
        title="Remove Kubernetes Integration"
        isOpen={dialogOpen}
        content={deleteDetails()}
        onConfirm={handleDelete}
        onClose={() => setDialogOpen(false)}
      />
    </Box>
  );
}

const NoDiscoveredAssets = () => {
  return (
    <Grid
      key="empty"
      sx={{
        p: 2,
        display: "flex",
        placeContent: "center",
      }}
      size={12}
    >
      You do not currently have any discovered assets
    </Grid>
  );
};

// Standard styling for each of the Table sections
type DataTableProps = {
  title: string;
  children: ReactNode;
};
const KubernetesDataTable = ({ title, children }: DataTableProps) => {
  return (
    <TableContainer>
      <Table
        sx={{
          "tr:hover": {
            background: "inherit",
          },
        }}
      >
        <TableHead sx={{ backgroundColor: "inherit" }}>
          <TableRow
            sx={{
              "&:hover": {
                pointerEvents: "none",
                background: "inherit",
              },
            }}
          >
            <TableCell
              colSpan={2}
              sx={{
                pt: 0,
                pl: 0,
                pb: 1,
                borderBottom: (theme) =>
                  `1px solid ${theme.palette.background.lightest}`,
                textTransform: "uppercase",
              }}
            >
              {title}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{children}</TableBody>
      </Table>
    </TableContainer>
  );
};

// We've added a new blue color to our palette but it doesn't yet appear
// anywhere else, so we're modifying the getColor function for this specific
// use case
const modifiedGetColor = (
  theme: Theme,
  status?: IntegrationMessageStatus | null,
) => {
  if (!status) return getColor(theme, "primaryText");
  switch (status.toLowerCase()) {
    case "info":
      return "#29B6F6";
    default:
      return getColor(theme, status);
  }
};
